import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articleData } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = Object.values(articleData).find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = article.seoTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.seoDescription);
      }
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `http://heiliaobao.cn/article/${article.slug}`);
      }

      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', article.seoTitle);
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute('content', article.seoDescription);
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) ogUrl.setAttribute('content', `http://heiliaobao.cn/article/${article.slug}`);
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = article.relatedArticles
    .map((id) => Object.values(articleData).find((a) => a.id === id))
    .filter(Boolean);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    author: {
      '@type': 'Person',
      name: article.author.name,
      jobTitle: article.author.role,
    },
    publisher: {
      '@type': 'Organization',
      name: '51爆料网',
      url: 'http://heiliaobao.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://heiliaobao.cn/article/${article.slug}`,
    },
  };

  const faqJsonLd = article.faq && article.faq.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: article.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      }
    : null;

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      {faqJsonLd && (
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      )}
      <main>
        <Navbar />

        <article>
          <header className="relative w-full pt-16">
            <div className="relative w-full overflow-hidden rounded-b-[2.5rem]">
              <div className="absolute inset-0">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
              </div>

              <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-6">
                <div className="pt-4">
                  <Breadcrumb
                    items={[
                      { name: '首页', path: '/' },
                      { name: article.category, path: '/category/all' },
                      { name: article.title },
                    ]}
                  />
                </div>
                <div className="flex flex-col lg:flex-row items-start gap-8 py-14 md:py-20">
                  <div className="lg:w-1/2">
                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-[1.08] tracking-tight">
                      {article.title}
                    </h1>
                  </div>
                  <div className="lg:w-1/2 flex flex-col justify-end pt-2 lg:pt-6">
                    <p className="text-background-100/80 text-base md:text-lg leading-relaxed mb-4 max-w-md">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-background-100/70 text-xs">
                      <span>{article.author.name}</span>
                      <span>·</span>
                      <time dateTime={article.publishedAt}>
                        {new Date(article.publishedAt).toLocaleDateString('zh-CN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </time>
                      <span>·</span>
                      <span>{Math.floor(article.views / 10000)}万阅读</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <section className="w-full py-10 md:py-14 bg-background-50">
            <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="lg:w-3/4">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs font-medium rounded-full">
                      独家原文
                    </span>
                    <span className={`px-3 py-1 text-background-50 text-xs font-semibold rounded-full ${article.tag === '独家' ? 'bg-primary-500' : 'bg-accent-500'}`}>
                      {article.tag}
                    </span>
                  </div>

                  <div
                    className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed article-content"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <div className="mt-10 pt-8 border-t border-background-200/70">
                    <div className="flex items-center gap-4 flex-wrap">
                      <img
                        src={article.author.avatar}
                        alt={article.author.name}
                        className="w-12 h-12 rounded-full object-cover object-top"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-950">{article.author.name}</p>
                        <p className="text-xs text-foreground-500">{article.author.role}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <aside className="lg:w-1/4">
                  <div className="bg-secondary-100/60 rounded-2xl p-6 sticky top-24">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs text-secondary-700 font-medium bg-secondary-200/60 px-2 py-0.5 rounded-full">主编按语</span>
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-secondary-400"></span>
                        <span className="w-1 h-1 rounded-full bg-secondary-400"></span>
                        <span className="w-1 h-1 rounded-full bg-secondary-400"></span>
                      </span>
                    </div>
                    <p className="text-sm text-foreground-800 leading-relaxed mb-6">
                      本报道经51爆料编辑团队多源交叉验证，信息可信度评级为A级。爆料内容基于可靠线人提供的第一手资料，部分细节因保护线人隐私已做脱敏处理。
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img
                          src={article.author.avatar}
                          alt={article.author.name}
                          className="w-8 h-8 rounded-full object-cover object-top"
                        />
                        <div>
                          <p className="text-xs font-semibold text-foreground-950">编辑审核</p>
                          <p className="text-[10px] text-foreground-500">51爆料编辑部</p>
                        </div>
                      </div>
                      <Link
                        to="/category/all"
                        className="px-3 py-1.5 bg-secondary-200/50 text-secondary-800 rounded-full text-xs font-medium hover:bg-secondary-300/50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        更多{article.category}
                        <span className="w-3 h-3 inline-flex items-center justify-center ml-0.5">
                          <i className="ri-arrow-right-line"></i>
                        </span>
                      </Link>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          {article.faq && article.faq.length > 0 && (
            <section className="w-full py-10 md:py-14 bg-background-50">
              <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
                <div className="relative bg-background-900 rounded-3xl overflow-hidden">
                  <div className="absolute top-0 left-0 w-64 h-64 opacity-5">
                    <span className="text-background-50 text-[200px] font-serif leading-none">?</span>
                  </div>
                  <div className="relative z-10 p-8 md:p-14">
                    <span className="text-6xl md:text-8xl font-serif text-background-100 leading-none block mb-2">&ldquo;</span>
                    <h3 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-8">常见问题解答</h3>
                    <div className="space-y-6">
                      {article.faq.map((item, i) => (
                        <div key={i}>
                          <h4 className="text-sm md:text-base font-semibold text-primary-300 mb-2">
                            {item.question}
                          </h4>
                          <p className="text-sm text-foreground-300 leading-relaxed">
                            {item.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {relatedArticles.length > 0 && (
            <section className="w-full bg-background-100 py-10 md:py-14">
              <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => {
                    if (!ra) return null;
                    return (
                      <Link
                        key={ra.id}
                        to="/category/all"
                        className="group rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 bg-background-50 border border-background-200/70"
                      >
                        <div className="relative h-[180px] overflow-hidden">
                          <img
                            src={ra.image}
                            alt={ra.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4">
                          <span className="text-xs text-primary-500 font-medium">{ra.category}</span>
                          <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 leading-snug mt-1">
                            {ra.title}
                          </h3>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </article>

        <Footer />
      </main>
    </>
  );
}